/**
 * Блоки подвкладки «Посещаемость» (DC → Юнифанс, 21.09, владелец): когда заходят,
 * где были, откуда приходят, как часто возвращаются и доходит ли это до дела.
 *
 * Всё меряем ЛЮДЬМИ, а не событиями: событий много от глобального трекера кликов
 * (одна игра = десятки tap), поэтому «где были» по событиям победил бы экран с самым
 * насыщенным интерфейсом, а не тот, ради которого открывают приложение.
 */
import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { HBars, Heatmap, NUM, fmtNum } from "./dcVisitsCharts";
import type { VisitsData } from "../../hooks/useUnifansVisits";

export const box: CSSProperties = { border: "1px solid var(--surface-3)", background: "var(--surface-1)", borderRadius: 8, padding: 10 };
export const cap: CSSProperties = { fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-3)", fontFamily: "var(--f-meta)" };
const hint: CSSProperties = { fontSize: 10.5, color: "var(--text-3)", marginTop: 6, lineHeight: 1.35 };

/** Названия экранов и каналов по-человечески; неизвестный ключ показываем как есть. */
export const SCREEN_RU: Record<string, string> = {
  session_detail: "Дело игры", "tab:materials": "Юнигазета", "tab:sessions": "Мои сессии", quest: "Карточка квеста",
  app: "Вход в приложение", "tab:catalog": "Каталог", actor_dossier: "Досье актёра", "tab:community": "Коммьюнити",
  quest_catalog: "Каталог квестов", special: "Спецверсия", link: "Ссылка", loft_promo: "Промо лофта", "—": "Без экрана",
};
export const CHAN_RU: Record<string, string> = {
  qr_mate: "QR игры", qr_owner: "QR капитана (старый)", recruit: "Личный QR оператора", quest: "Ссылка на квест",
  special: "Ссылка на спецверсию", uc_code: "UC-код", referral: "Приглашение друга", montage: "Монтаж",
  tagged: "Метка канала", direct: "Без метки — открыл из чатов", unknown: "Метка не разобрана",
};
export const ru = (map: Record<string, string>, k: string): string => map[k] ?? k;
const DOW = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

/** Когда заходят: день недели × час. Один день в периоде — та же карта в одну строку. */
export function WhenBlock({ data }: { data: VisitsData }): ReactElement {
  const [pick, setPick] = useState<string | null>(null);
  useEffect(() => setPick(null), [data]);   // новый период — старое число уже не про эту карту
  const oneDay = data.from === data.to;
  const cell = new Map(data.grid.map((g) => [`${g.d}_${g.h}`, g.visitors]));
  /* Сколько раз каждый день недели попал в период: иначе при 10 днях «понедельников» два,
     а «четвергов» один — и четверг выглядит тусклее только из-за длины окна. */
  const occ = Array.from({ length: 7 }, () => 0);
  for (let n = 0; n < data.days; n++) {
    const day = new Date(Date.parse(`${data.from}T00:00:00Z`) + n * 86400000);
    const idx = (day.getUTCDay() + 6) % 7;
    occ[idx] = (occ[idx] ?? 0) + 1;
  }
  const cells = oneDay
    ? [data.hours.map((h) => h.visitors)]
    : DOW.map((_, r) => Array.from({ length: 24 }, (_, h) => cell.get(`${r + 1}_${h}`) ?? 0));
  const shade = oneDay ? cells : cells.map((row, r) => row.map((v) => (occ[r] ? v / occ[r]! : 0)));
  const max = Math.max(1, ...shade.flat());
  return (
    <section style={box}>
      <div style={cap}>Когда заходят · посетители</div>
      <div style={{ marginTop: 8 }}>
        <Heatmap cells={cells} shade={shade} rowLabels={oneDay ? ["день"] : DOW}
          colLabels={data.hours.map((h) => String(h.k).padStart(2, "0"))} max={max} onPick={setPick} />
      </div>
      <div style={{ ...hint, minHeight: 14 }}>{pick ?? (oneDay ? "Часы выбранного дня." : "Плотнее клетка — больше людей заходило в этот час этого дня недели.")}</div>
    </section>
  );
}

/** Где были: экраны по числу людей; событий — вторым числом, чтобы не путать с людьми. */
export function ScreensBlock({ data }: { data: VisitsData }): ReactElement {
  const rows = data.screens.slice(0, 9).map((s) => ({ label: ru(SCREEN_RU, s.k), value: s.people, sub: `${fmtNum(s.n)} действий` }));
  return (
    <section style={box}>
      <div style={cap}>Где были · сколько человек открывали раздел</div>
      <div style={{ marginTop: 6 }}><HBars rows={rows} unit="чел." /></div>
      <div style={hint}>Один человек попадает в несколько разделов — сумма больше числа посетителей.</div>
    </section>
  );
}

/** Откуда приходят: канал входа известен только у открытий приложения. */
export function ChannelsBlock({ data }: { data: VisitsData }): ReactElement {
  const rows = data.channels.map((ch) => ({ label: ru(CHAN_RU, ch.k), value: ch.people, sub: `${fmtNum(ch.n)} открытий` }));
  const noMark = data.channelsTotal.noMark;
  const all = data.channelsTotal.people || 1;
  return (
    <section style={box}>
      <div style={cap}>Откуда приходят · вход в приложение</div>
      <div style={{ marginTop: 6 }}><HBars rows={rows} color="var(--chart-3)" unit="чел." /></div>
      <div style={hint}>
        Метка берётся из ссылки, по которой открыли приложение. Вернувшийся открывает Юнифанс прямо из чатов Telegram —
        такой вход без метки ({Math.round((noMark / all) * 100)}% людей), это привычка возвращаться, а не «сам нашёл».
      </div>
    </section>
  );
}

/** Как часто заходят: сколько людей были активны 1 день, 2–3, 4–7, 8+. */
export function FrequencyBlock({ data }: { data: VisitsData }): ReactElement {
  const bucket = (d: number) => (d <= 1 ? "1 день" : d <= 3 ? "2–3 дня" : d <= 7 ? "4–7 дней" : "8+ дней");
  const order = ["1 день", "2–3 дня", "4–7 дней", "8+ дней"];
  const acc = new Map<string, number>();
  for (const f of data.frequency) acc.set(bucket(f.days), (acc.get(bucket(f.days)) ?? 0) + f.people);
  const rows = order.filter((k) => acc.has(k)).map((k) => ({ label: k, value: acc.get(k) ?? 0 }));
  const r = data.retention;
  return (
    <section style={box}>
      <div style={cap}>Как часто заходят · за период</div>
      <div style={{ marginTop: 6 }}><HBars rows={rows} color="var(--chart-4)" unit="чел." /></div>
      <div style={{ ...NUM, fontSize: 12, color: "var(--text-1)", marginTop: 8 }}>
        Из {fmtNum(r.yesterday)} вчерашних вернулись <b>{fmtNum(r.returned)}</b>
        {r.yesterday > 0 ? <span style={{ color: "var(--text-3)" }}> · {Math.round((r.returned / r.yesterday) * 100)}%</span> : null}
      </div>
      <div style={hint}>Считаем по последнему дню периода ({r.day.slice(8, 10)}.{r.day.slice(5, 7)}).</div>
    </section>
  );
}

/** Доходит ли посещаемость до дела: люди, а не события; ступени не вложены друг в друга. */
export function FunnelBlock({ data }: { data: VisitsData }): ReactElement {
  const f = data.funnel;
  const rows = [
    { label: "Зашли", value: f.visitors },
    { label: "Читали Юнигазету", value: f.paper },
    { label: "Смотрели квесты", value: f.quest },
    { label: "Вступили в фан-клуб", value: f.fan },
    { label: "Чаевые актёру", value: f.tip },
    { label: "Покупка в Лавке", value: f.shop },
    { label: "Заявка на игру", value: f.book },
  ];
  return (
    <section style={box}>
      <div style={cap}>Доходит до дела · людей за период</div>
      <div style={{ marginTop: 6 }}><HBars rows={rows} color="var(--chart-2)" unit="чел." /></div>
      <div style={hint}>Ступени независимы: человек может читать газету и не смотреть квесты. Ноль в заявках — значит из приложения за период не бронировали.</div>
    </section>
  );
}
